<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :fetchCallback="fetchCallback"
            :columnDefs="columnDefs"
            :isAutoWidth="true"
            :suppressScrollOnNewData="false"
            :maxBlocksInCache="null"
        >
        </grid>
    </page>
</template>

<script>
import isArray from 'lodash/isArray';
import startCase from 'lodash/startCase';
import capitalize from 'lodash/capitalize';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import isBoolean from 'lodash/isBoolean';
import { mapGetters } from 'vuex';

import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import api from '../../../api';

import { REPORT_SETTINGS_ENDPOINT } from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';

export default {
    components: {
        Page,
        Grid,
        actionsCellRenderer: ActionsCellRenderer,
    },
    data() {
        return {
            rowData: [],
            busy: false,
            tabs: [
                {
                    title: 'Bookmarked Reports',
                    active: true,
                },
            ],
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        columnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'Name',
                },
                {
                    field: 'report_sysname',
                    headerName: 'Report Sysname',
                    sort: 'asc',
                },
                {
                    field: 'type',
                    headerName: 'Type',
                },
                {
                    field: 'settings',
                    headerName: 'Settings',
                    autoHeight: true,
                    autoWidthExclude: true,
                    cellRenderer: ({ data }) => {
                        const settings = data.settings;
                        const arr = JSON.parse(settings);
                        return arr
                            .map((item) => {
                                return item.label && ` ${item.label}: ${this.generateSettingsValueLabel(item)}`;
                            })
                            .filter((item) => !!item)
                            .join('<br/>');
                    },
                },
                {
                    field: '',
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        deleteSettings: async (data) => {
                            try {
                                this.busy = true;
                                await api.delete(REPORT_SETTINGS_ENDPOINT(data.id));
                                this.$refs.grid.refreshDataSource();
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text: generic || 'Could not delete settings',
                                });
                            } finally {
                                this.busy = false;
                            }
                        },
                        updateSettings: async () => {
                            this.$refs.grid.refreshDataSource();
                        },
                    },
                },
            ];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;

                return api.get(`${REPORT_SETTINGS_ENDPOINT()}grid/`, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });
            };
        },
    },

    methods: {
        generateSettingsValueLabel(item) {
            const { value, placeholder } = item;
            const parsedDate = parseISO(value);
            const isValidDate = isValid(parsedDate);
            if (placeholder && !value) {
                return placeholder;
            }
            if (isValidDate) {
                return format(parsedDate, this.userUnicodeDateFormat);
            }
            if (isArray(value)) {
                return value.length ? capitalize(startCase(item.value.join(','))) : 'All';
            }
            if (isBoolean(value)) {
                return value ? 'Yes' : 'No';
            }
            return capitalize(startCase(value));
        },
    },
};
</script>

<style>
</style>